import { CrescLoader } from "../components/LoadingScreen";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Crown } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useEntitlements } from "../context/EntitlementsContext";
import { toast } from "sonner";

/**
 * Pricing page — `/pricing`. Loads `/api/plans` and renders the 4 cards in
 * a single horizontal strip. Clicking Upgrade hits
 * `/api/subscriptions/checkout`, then redirects to Stripe Checkout.
 *
 * Business plan asks for a seats input (min 2). All other plans default
 * to 1 seat.
 */

const FEATURE_LABELS = {
  triggers_actions: "Triggers and Actions",
  secure_sharing: "Secure Sharing",
  day_tints: "Day Tints",
  spotlight_ai: "Spotlight AI",
  highlight_roster: "Highlight Roster",
  custom_fonts: "Custom Fonts",
  otp: "Over-the-Phone Support",
  integrations: "Slack & GitHub Integrations",
  email_embedding: "Email Embedding",
  priority_email: "High-Priority Email Support",
  team: "Team Workspace",
  free_pro_seats: "Free personal Pro account per seat" };

const LIMIT_LABELS = (limits) => {
  const fmt = (v, unit, suffix = "") => v === -1 ? `Unlimited ${unit}${suffix}` : `${v} ${unit}${suffix}`;
  return [
    fmt(limits.workspaces, "workspaces"),
    fmt(limits.templates, "templates"),
    fmt(limits.documents, "documents"),
    fmt(limits.tabs, "tabs"),
    limits.key_pairs === -1 ? "Unlimited key-pair values" : `${limits.key_pairs} key-pair values`,
    limits.version_days === -1 ? "All-time version history" : `Version history up to ${limits.version_days} days`,
    limits.resources_per_node === -1 ? "Unlimited resources per node" : `${limits.resources_per_node} resources per node`,
    limits.time_zones === -1 ? "Unlimited time zone views" : (limits.time_zones > 1 ? `${limits.time_zones} time zone views` : null),
  ].filter(Boolean);
};

export default function PricingPage() {
  const { user } = useAuth();
  const ent = useEntitlements();
  const nav = useNavigate();
  const [plans, setPlans] = useState({});
  const [seats, setSeats] = useState(2);
  const [loading, setLoading] = useState(null); // plan_id while submitting

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
  }, []);

  const upgrade = async (plan_id) => {
    if (!user) {
      // Stash where we came from so /login → /pricing after auth.
      nav("/login", { state: { from: "/pricing" } });
      return;
    }
    setLoading(plan_id);
    try {
      const body = { plan_id, origin_url: window.location.origin };
      if (plan_id === "business") body.seats = Math.max(2, seats);
      const { data } = await api.post("/subscriptions/checkout", body);
      if (data.free) {
        toast.success("Now on Free plan");
        await ent.reload();
        nav("/app");
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
    }
    setLoading(null);
  };

  const ORDER = ["free", "standard", "pro", "business"];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#EDEDED] p-8" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-semibold">Pricing</h1>
            <p className="text-sm text-[#A1A1AA] mt-1">Choose the plan that fits your workflow.</p>
          </div>
          <button
            onClick={() => nav("/app")}
            className="text-xs text-[#A1A1AA] hover:text-[#EDEDED] underline-offset-2 hover:underline"
            data-testid="pricing-back-btn"
          >
            ← Back to app
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ORDER.map((plan_id) => {
            const p = plans[plan_id];
            if (!p) return null;
            const isCurrent = ent.plan === plan_id;
            const featureList = LIMIT_LABELS(p.limits);
            return (
              <div
                key={plan_id}
                className={`rounded-lg border ${isCurrent ? "border-[#5A90F5]" : "border-[#2A2A2A]"} bg-[#141414] p-5 flex flex-col`}
                data-testid={`plan-card-${plan_id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h2 className="font-display text-lg font-semibold">{p.name}</h2>
                  {isCurrent && (
                    <span className="px-2 py-0.5 rounded-full bg-[#5A90F5]/15 text-[10px] uppercase tracking-wider text-[#5A90F5]">Current</span>
                  )}
                  {plan_id === "pro" && !isCurrent && (
                    <Crown className="w-4 h-4 text-[#FFCC00]" />
                  )}
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-display font-bold">${p.amount.toFixed(0)}</span>
                  {p.amount > 0 && (
                    <span className="text-xs text-[#A1A1AA] ml-1">
                      /{plan_id === "business" ? "seat/" : ""}month
                    </span>
                  )}
                </div>
                {plan_id === "business" && (
                  <div className="mb-3" data-testid="business-seats-row">
                    <label className="block meta-label mb-1">Team Size (min 2)</label>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setSeats((s) => Math.max(2, s - 1))}
                        disabled={seats <= 2}
                        className="w-7 h-7 rounded border border-[#2A2A2A] bg-[#0A0A0A] text-[#A1A1AA] hover:bg-[#1a1a1a] disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                        data-testid="business-seats-decrement"
                      >−</button>
                      <input
                        type="number"
                        min={2}
                        value={seats}
                        onChange={(e) => setSeats(Math.max(2, parseInt(e.target.value) || 2))}
                        className="cresc-no-spinner flex-1 text-center bg-[#0A0A0A] border border-[#2A2A2A] rounded px-2 py-1 text-xs outline-none focus:border-[#5A90F5] font-mono"
                        data-testid="business-seats-input"
                      />
                      <button
                        type="button"
                        onClick={() => setSeats((s) => s + 1)}
                        className="w-7 h-7 rounded border border-[#2A2A2A] bg-[#0A0A0A] text-[#A1A1AA] hover:bg-[#1a1a1a] text-sm"
                        data-testid="business-seats-increment"
                      >+</button>
                    </div>
                    {/* Live total — recalculated on every keystroke / +/-
                        click so the user sees what they'll actually pay
                        before hitting Upgrade. */}
                    <div className="mt-2 flex items-baseline justify-between text-[11px]" data-testid="business-seats-total-row">
                      <span className="text-[#A1A1AA]">${p.amount.toFixed(0)} × {seats} seats =</span>
                      <span className="font-display font-semibold text-[#EDEDED]" data-testid="business-seats-total">${(p.amount * seats).toFixed(0)}/mo</span>
                    </div>
                    <p className="mt-1 text-[10px] text-[#52525B]" data-testid="business-seats-perk">
                      Each seat unlocks a free personal Pro account (kept for 1 month after removal).
                    </p>
                  </div>
                )}
                <ul className="space-y-1.5 text-xs flex-1">
                  {featureList.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-3 h-3 text-[#5A90F5] mt-0.5 flex-shrink-0" />
                      <span className="text-[#EDEDED]">{f}</span>
                    </li>
                  ))}
                  {/* Show EVERY feature flag so users can see at a glance
                      what's in vs out (user request iter32: "Pricing
                      plans should have an ✗ next to features that it
                      does NOT include"). */}
                  {Object.entries(p.features).map(([k, v]) => (
                    <li key={k} className={`flex items-start gap-2 ${v ? "" : "opacity-45"}`}>
                      {v
                        ? <Check className="w-3 h-3 text-[#5A90F5] mt-0.5 flex-shrink-0" />
                        : <X className="w-3 h-3 text-[#A1A1AA] mt-0.5 flex-shrink-0" />}
                      <span className={v ? "text-[#EDEDED]" : "text-[#A1A1AA] line-through"}>
                        {FEATURE_LABELS[k] || k}
                      </span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => upgrade(plan_id)}
                  disabled={loading === plan_id || isCurrent}
                  className={`mt-5 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    isCurrent
                      ? "bg-[#1a1a1a] text-[#A1A1AA] cursor-not-allowed"
                      : plan_id === "free"
                        ? "bg-[#1a1a1a] border border-[#2A2A2A] text-[#EDEDED] hover:bg-[#222]"
                        : "bg-[#5A90F5] text-white hover:bg-[#4a80e5]"
                  }`}
                  data-testid={`plan-upgrade-${plan_id}`}
                >
                  {loading === plan_id && <CrescLoader size={14} />}
                  {isCurrent
                    ? "Current plan"
                    : p.amount === 0
                      ? (ent.plan && ent.plan !== "free" ? "Downgrade to Free" : "Continue Free")
                      : plan_id === "business"
                        ? `Upgrade · $${(p.amount * seats).toFixed(0)}/mo`
                        : "Upgrade"}
                </button>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-[#52525B] text-center mt-6">
          Test mode — payments are simulated. Use card 4242 4242 4242 4242, any future expiry, any CVC.
        </p>
      </div>
    </div>
  );
}
